"use client";

import { Autocomplete, Card, FormControlLabel, Skeleton, Switch } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useFormikContext } from "formik";
import { useState } from "react";
import InputMaskWrapper from "@/components/atoms/InputMaskWrapper";
import { useNavigate } from "react-router-dom";

import FormField from "@/components/atoms/FormField";
import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDInput from "@/components/atoms/MDInput";
import MDTypography from "@/components/atoms/MDTypography";
import { LANGUAGES } from "@/constants/language";
import { LONG_MASK, SHORT_MASK } from "@/constants/phone.constants";
import ROUTE_URLS from "@/constants/route-urls";
import useLanguage from "@/contexts/language-context";
import { removeNonIntegersFromString } from "@/helpers/string-helpers";
import useGetAllProfiles from "@/hooks/requests/permissions/use-get-all-profiles";
import { UserFormMessages } from "@/messages/users/user-form.messages";

import { userFormNames } from "./form-data";
import { UserFormData } from "./types";

type Props = {
  messages: UserFormMessages;
  isLoading: boolean;
  isEditing?: boolean;
  showPermission?: boolean;
  isProfile?: boolean;
};

function UserForm({ messages, isLoading, showPermission = false, isProfile = false }: Props) {
  const router = useNavigate();
  const { lang } = useLanguage();
  const formik = useFormikContext<UserFormData>();
  const { values, errors, submitForm, handleChange, setFieldValue } = formik;

  const isPhone1LongMask = removeNonIntegersFromString(values.phone1 || "").length > 12;
  const isPhone2LongMask = removeNonIntegersFromString(values.phone2 || "").length > 12;

  const [phone1Mask, setPhone1Mask] = useState(isPhone1LongMask ? LONG_MASK : SHORT_MASK);
  const [phone2Mask, setPhone2Mask] = useState(isPhone2LongMask ? LONG_MASK : SHORT_MASK);

  const { data: permissions, isLoading: isLoadingPermissions } = useGetAllProfiles();

  const handlePhoneChanges = (name: string, value: string) => {
    const formattedPhone = removeNonIntegersFromString(value);

    if (name === "phone1") {
      if (formattedPhone.length > 10) {
        setPhone1Mask(LONG_MASK);
      } else {
        setPhone1Mask(SHORT_MASK);
      }
    } else if (name === "phone2") {
      if (formattedPhone.length > 10) {
        setPhone2Mask(LONG_MASK);
      } else {
        setPhone2Mask(SHORT_MASK);
      }
    }

    setFieldValue(name, formattedPhone);
  };

  const handleToggleSwitch = (permissionId: number, isChecked: boolean) => {
    if (isChecked) {
      const newUserProfile = values.userProfile.filter(
        (profile) => profile.profileId !== permissionId
      );
      setFieldValue("userProfile", newUserProfile);
    } else {
      const newUserProfile = [...values.userProfile, { profileId: permissionId }];
      setFieldValue("userProfile", newUserProfile);
    }
  };

  return (
    <MDBox>
      <Card>
        <MDBox p={3}>
          <MDTypography variant="h5">{messages.userCardTitle}</MDTypography>
        </MDBox>

        <MDBox component="form" pb={3} px={3}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <FormField
                placeholder={messages.namePlaceholder}
                fullWidth
                variant="standard"
                InputLabelProps={{ shrink: true }}
                label={messages.nameLabel}
                name={userFormNames.name}
                onChange={handleChange}
                value={values.name}
                error={!!errors.name}
                required
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <FormField
                placeholder={messages.emailPlaceholder}
                fullWidth
                variant="standard"
                InputLabelProps={{ shrink: true }}
                label={messages.emailLabel}
                name={userFormNames.email}
                onChange={handleChange}
                value={values.email}
                error={!!errors.email}
                required
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <InputMaskWrapper
                mask={phone1Mask}
                value={values.phone1}
                onChange={(e) => handlePhoneChanges(e.target.name, e.target.value)}
                name={userFormNames.phone1}
                fullWidth
                variant="standard"
                InputLabelProps={{ shrink: true }}
                label={messages.phoneLabel}
                error={!!errors.phone1}
                helperText={errors.phone1}
                required
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <InputMaskWrapper
                mask={phone2Mask}
                value={values.phone2}
                onChange={(e) => handlePhoneChanges(e.target.name, e.target.value)}
                name={userFormNames.phone2}
                fullWidth
                variant="standard"
                InputLabelProps={{ shrink: true }}
                label={messages.phone2Label}
                error={!!errors.phone2}
                helperText={errors.phone2}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                fullWidth
                options={LANGUAGES}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                value={LANGUAGES.find((option) => option.value === values.languageCode) || null}
                onChange={(_, newValue) => {
                  setFieldValue(userFormNames.languageCode, newValue ? newValue.value : "");
                }}
                renderInput={(params) => (
                  <MDInput
                    {...params}
                    label={messages.languageLabel}
                    variant="standard"
                    error={!!errors.languageCode}
                  />
                )}
              />
            </Grid>

            {showPermission && (
              <Grid size={12}>
                <MDBox py={2}>
                  <MDTypography variant="h5">{messages.permissionsTitle}</MDTypography>
                </MDBox>

                <MDBox>
                  <Grid container spacing={3} gap={2}>
                    {isLoadingPermissions
                      ? Array.from({ length: 6 }).map((_, index) => (
                          <Grid key={index} size={{ xs: 12, sm: 6 }}>
                            <Skeleton variant="rectangular" height={40} />
                          </Grid>
                        ))
                      : !!permissions &&
                        permissions.items
                          .slice()
                          .sort((a, b) => a.label.localeCompare(b.label))
                          .map((permission, index) => {
                            const isChecked = values.userProfile.some(
                              (profile) => profile.profileId === permission.id
                            );

                            return (
                              <Grid key={`${permission.code}-${index}`} size={{ xs: 12, sm: 6 }}>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={isChecked}
                                      onClick={() => handleToggleSwitch(permission.id, isChecked)}
                                    />
                                  }
                                  label={permission.label}
                                />
                              </Grid>
                            );
                          })}
                  </Grid>
                </MDBox>
              </Grid>
            )}

            {isProfile && (
              <Grid size={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!values.enableSoundNotification}
                      onChange={(e) => setFieldValue("enableSoundNotification", e.target.checked)}
                    />
                  }
                  label={messages.soundNotificationLabel}
                />
              </Grid>
            )}
          </Grid>
        </MDBox>
      </Card>

      <MDBox mt={2} display="flex" justifyContent="flex-end" gap={2}>
        <MDButton
          variant="gradient"
          color="light"
          onClick={() => router(ROUTE_URLS.users(lang))}
          isLoading={isLoading}
        >
          {messages.backButton}
        </MDButton>

        <MDButton variant="gradient" color="dark" onClick={submitForm} isLoading={isLoading}>
          {messages.updateButton}
        </MDButton>
      </MDBox>
    </MDBox>
  );
}

export default UserForm;
